/**
 * Pure (no `Intl`) English-month date formatters that match the locale-pinned
 * `Intl.DateTimeFormat("en", …)` output but never reach for `Intl`. Two
 * timezone flavors:
 *
 *  - `*Local` helpers use viewer-local getters (`getMonth()`, `getDate()`).
 *    Use for surfaces where the date is a calendar selection or "completed
 *    on" timestamp the user thinks of in their own timezone.
 *  - `*Utc` helpers use UTC getters (`getUTCMonth()`, etc.). Use for surfaces
 *    where the underlying value is a UTC calendar boundary (campaign
 *    promotional banner, Trade & Earn UTC date ranges).
 *
 * Companion to the existing `Intl`-backed UTC helpers in `intl-formats.ts`,
 * which stay unchanged for hub-v2 / cody consumers.
 */

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
const MONTHS_UPPER = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"] as const;

function toDate(input: Date | string | number): Date {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) {
    throw new RangeError(`Invalid date input: ${String(input)}`);
  }
  return d;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** `Apr 22` — local timezone. */
export function formatShortDateLocal(input: Date | string | number): string {
  const d = toDate(input);
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}`;
}

/** `Apr 22, 2027` — local timezone. */
export function formatFullDateLocal(input: Date | string | number): string {
  const d = toDate(input);
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/**
 * Compact local range:
 *  - same year → `Apr 22 – May 23`
 *  - cross year → `Dec 28, 2026 – Jan 4, 2027`
 */
export function formatShortDateRangeLocal(start: Date | string | number, end: Date | string | number): string {
  const a = toDate(start);
  const b = toDate(end);
  if (a.getFullYear() === b.getFullYear()) {
    return `${formatShortDateLocal(a)} – ${formatShortDateLocal(b)}`;
  }
  return `${formatFullDateLocal(a)} – ${formatFullDateLocal(b)}`;
}

/** Full local range with year on both ends (date picker button label). */
export function formatFullDateRangeLocal(start: Date | string | number, end: Date | string | number): string {
  return `${formatFullDateLocal(start)} – ${formatFullDateLocal(end)}`;
}

/** `Apr` — calendar month dropdown. */
export function formatMonthShortLocal(input: Date | string | number): string {
  return MONTHS_SHORT[toDate(input).getMonth()]!;
}

/** `2026-04-22` — local-calendar ISO date for stable `data-day` attributes. */
export function formatIsoDateLocal(input: Date | string | number): string {
  const d = toDate(input);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** `Apr 22, 14:30` — local short date + 24h time (bot rating form). */
export function formatShortDateTimeLocal(input: Date | string | number): string {
  const d = toDate(input);
  return `${formatShortDateLocal(d)}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** `2:00 PM` — local 12-hour clock time (calendar-style event time). */
export function formatTime12hLocal(input: Date | string | number): string {
  const d = toDate(input);
  const hours = d.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;
  return `${hours12}:${pad2(d.getMinutes())} ${ampm}`;
}

/** `7 PM` — a whole hour with no minutes, for an hour-of-day axis. Takes the hour, not a Date: callers label a bucket. */
export function formatHour12(hour: number): string {
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
    throw new RangeError(`Invalid hour: ${String(hour)}`);
  }
  return `${hour % 12 || 12} ${hour >= 12 ? "PM" : "AM"}`;
}

/** `15:30` — local 24-hour clock time (schedule/session start times). */
export function formatTime24hLocal(input: Date | string | number): string {
  const d = toDate(input);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** `13 MAY` — campaign banner header style, UTC getters. */
export function formatDayMonthUpperUtc(input: Date | string | number): string {
  const d = toDate(input);
  return `${pad2(d.getUTCDate())} ${MONTHS_UPPER[d.getUTCMonth()]}`;
}

/** `Sep` — UTC month, for a UTC start time printed with an explicit `UTC`. */
export function formatMonthShortUtc(input: Date | string | number): string {
  return MONTHS_SHORT[toDate(input).getUTCMonth()]!;
}

/** `12:00` — UTC 24-hour clock time. */
export function formatTime24hUtc(input: Date | string | number): string {
  const d = toDate(input);
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}
